import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Calendar, Anchor, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { SHIP_BY_SLUG, SHIPS } from "@/data/ships";
import AudioGuide from "@/components/AudioGuide";
import useSwipe from "@/hooks/useSwipe";
import NotFound from "@/pages/NotFound";

export default function ShipDetail() {
  const { slug } = useParams();
  const ship = SHIP_BY_SLUG[slug];
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const swipeRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLightboxIdx(null);
    // Ship detail pages are exclusively part of Chapter I — lock the
    // palette to Navy so it doesn't pick up Chapter II/III from prior scroll.
    document.documentElement.setAttribute("data-chapter", "i");
  }, [slug]);

  const total = ship?.images?.length || 0;
  const goLb = (delta) => {
    setLightboxIdx((i) =>
      i === null || total === 0 ? null : (i + delta + total) % total
    );
  };
  useSwipe(swipeRef, () => goLb(-1), () => goLb(1));

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") goLb(-1);
      else if (e.key === "ArrowRight") goLb(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ship) return <NotFound />;

  const idx = SHIPS.findIndex((s) => s.slug === slug);
  const prev = idx > 0 ? SHIPS[idx - 1] : null;
  const next = idx < SHIPS.length - 1 ? SHIPS[idx + 1] : null;
  const lightbox = lightboxIdx !== null ? ship.images[lightboxIdx] : null;

  return (
    <main className="pt-14 sm:pt-16 bg-[var(--c-bg)]" data-testid="ship-detail-page">
      {/* Hero */}
      <section className="relative h-[55vh] xs:h-[60vh] md:h-[75vh] min-h-[420px] sm:min-h-[480px] overflow-hidden">
        <img
          src={ship.images[0].src}
          alt={ship.name}
          className="absolute inset-0 w-full h-full object-cover archival-img"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--c-bg)]/60 via-[var(--c-bg)]/40 to-[var(--c-bg)]" />

        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto w-full px-5 sm:px-6 md:px-10 lg:px-16 pb-8 sm:pb-12 md:pb-20">
            <Link
              to="/#timeline"
              className="inline-flex items-center gap-2 overline mb-4 sm:mb-6 hover:text-[var(--c-accent-bright)] active:text-[var(--c-accent-bright)] transition min-h-[44px] py-2"
              data-testid="back-to-timeline-link"
            >
              <ArrowLeft size={14} /> Back to Timeline
            </Link>
            <p className="overline mb-2 sm:mb-3" data-testid="ship-dates">
              {ship.dates}
            </p>
            <h1 className="font-serif-display text-4xl xs:text-5xl md:text-7xl text-[var(--c-text)] tracking-tight leading-[0.95]">
              {ship.name}
              {ship.pennant && ship.pennant !== "Shore Establishment" && (
                <span className="block text-xl xs:text-2xl md:text-3xl italic text-[var(--c-accent)] mt-2 sm:mt-3">
                  {ship.pennant}
                </span>
              )}
            </h1>
          </div>
        </div>
      </section>

      {/* Particulars */}
      <section className="border-y border-[var(--c-border)] bg-[var(--c-bg-elev-2)]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 lg:px-16 py-8 sm:py-10 grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          <Particular icon={<Anchor size={16} />} label="Class / Type" value={ship.type} />
          <Particular icon={<MapPin size={16} />} label="Operating Area" value={ship.location} />
          <Particular icon={<Calendar size={16} />} label="Period" value={ship.dates} />
          <Particular icon={<Star size={16} />} label="Rate / Role" value={ship.role} gold />
        </div>
      </section>

      {/* Body */}
      <section className="py-14 sm:py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-5 sm:px-6">
          <div className="mb-10 sm:mb-12">
            <AudioGuide key={ship.slug} id={ship.slug} />
          </div>
          <p className="font-serif-display italic text-lg sm:text-2xl md:text-3xl text-[var(--c-text)]/85 leading-relaxed">
            {ship.summary}
          </p>

          {/* Dad's actual portrait at this establishment, when one exists */}
          {ship.dadPortrait && (
            <figure
              className="mt-10 sm:mt-14 grid xs:grid-cols-[180px,1fr] sm:grid-cols-[220px,1fr] gap-6 sm:gap-8 items-center"
              data-testid="dad-portrait-feature"
            >
              <div
                className="polaroid mx-auto xs:mx-0"
                style={{
                  background: "var(--c-text)",
                  padding: "10px 10px 36px 10px",
                  boxShadow:
                    "0 14px 32px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.18)",
                  transform: "rotate(-1.6deg)",
                }}
              >
                <img
                  src={ship.dadPortrait.src}
                  alt={ship.dadPortrait.caption}
                  loading="eager"
                  className="block w-[160px] h-[200px] xs:w-[200px] xs:h-[250px] sm:w-[220px] sm:h-[275px] object-cover"
                  style={{ filter: "grayscale(0.2) sepia(0.1) contrast(1.05)" }}
                />
                <figcaption
                  className="font-mono-plex mt-3 text-[10px] uppercase tracking-[0.18em] text-center"
                  style={{ color: "var(--c-bg)" }}
                >
                  T. P. Field · 1962
                </figcaption>
              </div>
              <div>
                <p className="overline mb-2">A Photograph</p>
                <h3 className="font-serif-display text-xl sm:text-2xl md:text-3xl text-[var(--c-text)] leading-tight">
                  Dad, aged sixteen
                </h3>
                <p className="mt-3 sm:mt-4 text-[var(--c-text-muted)] text-sm sm:text-base leading-relaxed">
                  His official portrait from HMS Ganges in 1962 — the cap
                  tally reads <span className="font-mono-plex text-[var(--c-accent)]">H.M.S. GANGES</span>,
                  the wide collar with three white stripes is the traditional
                  square-rig of a junior rating, and the smile gives him away.
                </p>
                <p className="mt-3 text-[var(--c-text-muted)] text-sm sm:text-base leading-relaxed italic">
                  Sixteen years of Royal Navy service started right here.
                </p>
              </div>
            </figure>
          )}

          <div className="hairline my-8 sm:my-12 max-w-[120px]" />

          <h2 className="font-serif-display text-2xl sm:text-3xl md:text-4xl text-[var(--c-accent)]">
            History
          </h2>
          <div className="mt-5 sm:mt-6 space-y-4 sm:space-y-5 text-[var(--c-text-muted)] text-sm sm:text-base md:text-lg leading-relaxed">
            {ship.history.map((p, i) => (
              <p key={i} className={i === 0 ? "drop-cap" : undefined}>
                {p}
              </p>
            ))}
          </div>

          <div className="hairline my-8 sm:my-12 max-w-[120px]" />

          <h2 className="font-serif-display text-2xl sm:text-3xl md:text-4xl text-[var(--c-accent)]">
            Dad's Role
          </h2>
          <p className="mt-5 sm:mt-6 text-[var(--c-text)] text-sm sm:text-base md:text-lg leading-relaxed">
            {ship.role_detail}
          </p>
        </div>
      </section>

      {/* Dad's first-hand memories of this ship */}
      {ship.memories && ship.memories.length > 0 && (
        <section className="pb-14 sm:pb-20 md:pb-28">
          <div className="max-w-3xl mx-auto px-5 sm:px-6">
            <div className="hairline mb-10 sm:mb-14 max-w-[120px]" />
            <p className="overline mb-3">In Dad's Own Memory</p>
            <h2 className="font-serif-display text-2xl sm:text-3xl md:text-4xl text-[var(--c-accent)]">
              What he remembers
            </h2>

            <ol className="mt-8 sm:mt-10 space-y-8 sm:space-y-10">
              {ship.memories.map((m, i) => (
                <li
                  key={m.date + i}
                  className="relative pl-6 sm:pl-8 border-l-2 border-[var(--c-accent)]/40"
                  data-testid={`memory-${i}`}
                >
                  <span
                    className="absolute -left-[7px] top-2 w-3 h-3 rounded-full bg-[var(--c-accent)] ring-4 ring-[var(--c-bg)]"
                    aria-hidden="true"
                  />
                  <p className="font-mono-plex text-[11px] sm:text-xs uppercase tracking-[0.2em] text-[var(--c-accent)]">
                    {m.date}
                  </p>
                  <h3 className="font-serif-display text-lg sm:text-xl md:text-2xl text-[var(--c-text)] mt-1 sm:mt-2 leading-snug">
                    {m.title}
                  </h3>
                  <p className="mt-3 text-sm sm:text-base text-[var(--c-text-muted)] leading-relaxed">
                    {m.body}
                  </p>
                  {m.photo && (
                    <figure
                      className="mt-5 sm:mt-6 border border-[var(--c-border)] bg-[var(--c-bg-elev)] relative"
                      data-testid={`memory-${i}-photo`}
                    >
                      {m.photo.isDad && (
                        <span className="dad-badge absolute top-2 left-2 z-10">
                          Dad's photo
                        </span>
                      )}
                      <img
                        src={m.photo.src}
                        alt={m.photo.caption}
                        loading="lazy"
                        className="archival-img w-full h-auto block"
                      />
                      {m.photo.caption && (
                        <figcaption className="p-3 sm:p-4 text-xs sm:text-sm text-[var(--c-text-muted)] leading-relaxed italic border-t border-[var(--c-border)]">
                          {m.photo.caption}
                        </figcaption>
                      )}
                    </figure>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* Air Group — what Dad would have seen flying off the deck */}
      {ship.airGroup && ship.airGroup.length > 0 && (
        <section className="pb-14 sm:pb-20 md:pb-28">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 lg:px-16">
            <div className="flex items-baseline justify-between mb-6 sm:mb-8 gap-4">
              <h2 className="font-serif-display text-xl sm:text-2xl md:text-3xl text-[var(--c-text)]">
                Aircraft on the Flight Deck
              </h2>
              <p className="overline text-[var(--c-text-muted)] text-right">
                {ship.airGroup.length} types
              </p>
            </div>
            <p className="text-[var(--c-text-muted)] text-sm sm:text-base mb-6 sm:mb-8 max-w-2xl italic">
              The aircraft Dad would have seen flying off Centaur's deck.
            </p>
            <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
              {ship.airGroup.map((a, i) => (
                <div
                  key={a.name + i}
                  className="border border-[var(--c-border)] bg-[var(--c-bg-elev)] overflow-hidden"
                  data-testid={`aircraft-${i}`}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-[var(--c-bg-elev-2)]">
                    <img
                      src={a.image}
                      alt={a.name}
                      loading="lazy"
                      className="archival-img w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-serif-display text-lg sm:text-xl text-[var(--c-text)] leading-tight">
                        {a.name}
                      </h3>
                      {a.confirmedByDad && (
                        <span className="font-mono-plex text-[9px] uppercase tracking-[0.18em] text-[var(--c-accent)] border border-[var(--c-accent)]/50 px-2 py-1 whitespace-nowrap shrink-0">
                          Dad's memory
                        </span>
                      )}
                      {a.guessedByDad && (
                        <span className="font-mono-plex text-[9px] uppercase tracking-[0.18em] text-[var(--c-text-muted)] border border-[var(--c-border)] px-2 py-1 whitespace-nowrap shrink-0">
                          Likely
                        </span>
                      )}
                    </div>
                    <p
                      className="overline mt-1"
                      dangerouslySetInnerHTML={{ __html: a.role }}
                    />
                    <p className="mt-3 text-sm text-[var(--c-text-muted)] leading-relaxed">
                      {a.detail}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Image strip */}
      {ship.images.length > 1 && (
        <section className="pb-14 sm:pb-20 md:pb-28">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 lg:px-16">
            <div className="flex items-baseline justify-between mb-6 sm:mb-8 gap-4">
              <h2 className="font-serif-display text-xl sm:text-2xl md:text-3xl text-[var(--c-text)]">
                Photographs
              </h2>
              <p className="overline text-[var(--c-text-muted)] text-right">
                {ship.images.length} images
              </p>
            </div>
            <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
              {ship.images.map((img, i) => (
                <button
                  key={img.src + i}
                  onClick={() => setLightboxIdx(i)}
                  className="group block border border-[var(--c-border)] overflow-hidden bg-[var(--c-bg-elev)] text-left active:scale-[0.98] transition-transform relative"
                  data-testid={`ship-photo-${i}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={img.src}
                      alt={img.caption || ship.name}
                      loading="lazy"
                      className="archival-img w-full h-full object-cover"
                    />
                  </div>
                  {img.isDad && (
                    <span
                      className="dad-badge absolute top-2 left-2"
                      data-testid={`ship-photo-${i}-dad-badge`}
                    >
                      Dad's photo
                    </span>
                  )}
                  {img.caption && (
                    <p className="p-3 sm:p-4 text-xs sm:text-sm text-[var(--c-text-muted)] leading-snug group-hover:text-[var(--c-text)] transition">
                      {img.caption}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </div>

          <Dialog
            open={lightboxIdx !== null}
            onOpenChange={(v) => !v && setLightboxIdx(null)}
          >
            <DialogContent className="w-[96vw] max-w-5xl max-h-[92vh] bg-[var(--c-bg)] border border-[var(--c-border)] p-0 overflow-hidden flex flex-col">
              {lightbox && (
                <>
                  <DialogTitle className="sr-only">
                    {ship.name} — photograph {lightboxIdx + 1} of {total}
                  </DialogTitle>
                  <div
                    ref={swipeRef}
                    className="relative bg-[var(--c-bg)] flex-1 min-h-0 flex items-center justify-center select-none touch-pan-y p-3 sm:p-4"
                  >
                    <img
                      src={lightbox.src}
                      alt={lightbox.caption || ship.name}
                      draggable={false}
                      className="max-w-full max-h-full w-auto h-auto object-contain pointer-events-none"
                    />
                    {total > 1 && (
                      <>
                        <button
                          onClick={() => goLb(-1)}
                          className="lb-arrow lb-arrow-left"
                          aria-label="Previous photograph"
                          data-testid="ship-lightbox-prev-btn"
                        >
                          <ChevronLeft size={22} />
                        </button>
                        <button
                          onClick={() => goLb(1)}
                          className="lb-arrow lb-arrow-right"
                          aria-label="Next photograph"
                          data-testid="ship-lightbox-next-btn"
                        >
                          <ChevronRight size={22} />
                        </button>
                      </>
                    )}
                  </div>
                  <div className="p-4 sm:p-6 md:p-8 border-t border-[var(--c-border)] shrink-0">
                    <p className="overline" data-testid="ship-lightbox-counter">
                      {ship.dates} · Plate {lightboxIdx + 1} / {total}
                    </p>
                    <p className="font-serif-display text-lg sm:text-xl md:text-2xl text-[var(--c-text)] mt-2">
                      {ship.name}
                    </p>
                    {lightbox.caption && (
                      <p className="text-[var(--c-text-muted)] mt-2 sm:mt-3 text-sm md:text-base">
                        {lightbox.caption}
                      </p>
                    )}
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>
        </section>
      )}

      {/* Prev/Next */}
      <section className="border-t border-[var(--c-border)] bg-[var(--c-bg-elev-2)]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 lg:px-16 py-10 sm:py-12 grid grid-cols-2 gap-4 sm:gap-6">
          <div>
            {prev && (
              <Link
                to={`/ships/${prev.slug}`}
                className="group block min-h-[60px]"
                data-testid="prev-ship-link"
              >
                <p className="overline mb-1 sm:mb-2">← Previous</p>
                <p className="font-serif-display text-base sm:text-xl md:text-2xl text-[var(--c-text)] group-hover:text-[var(--c-accent)] active:text-[var(--c-accent)] transition leading-tight">
                  {prev.name}
                </p>
                <p className="text-xs sm:text-sm text-[var(--c-text-muted)] mt-1">{prev.dates}</p>
              </Link>
            )}
          </div>
          <div className="text-right">
            {next && (
              <Link
                to={`/ships/${next.slug}`}
                className="group block min-h-[60px]"
                data-testid="next-ship-link"
              >
                <p className="overline mb-1 sm:mb-2">Next →</p>
                <p className="font-serif-display text-base sm:text-xl md:text-2xl text-[var(--c-text)] group-hover:text-[var(--c-accent)] active:text-[var(--c-accent)] transition leading-tight">
                  {next.name}
                </p>
                <p className="text-xs sm:text-sm text-[var(--c-text-muted)] mt-1">{next.dates}</p>
              </Link>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}

function Particular({ icon, label, value, gold }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-1 text-[var(--c-accent)] shrink-0">{icon}</span>
      <div className="min-w-0">
        <p className="overline mb-1">{label}</p>
        <p
          className={`font-serif-display text-sm sm:text-base md:text-lg leading-snug ${
            gold ? "text-[var(--c-accent)]" : "text-[var(--c-text)]"
          }`}
        >
          {value}
        </p>
      </div>
    </div>
  );
}
